import { useState, useEffect, memo } from 'react';
import { Info } from 'lucide-react';
import { fetchSurvivalAnalysis, fetchORRData } from '../services/survivalAnalysisService';
import { post } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import RiskTable from './common/RiskTable';
import HospitalDistributionChart from './HospitalDistributionChart';
import { buildRiskTableCohorts, isValidKMObject } from '../utils/riskTableHelpers';
import { LinesOfTherapySection, SafetyProfileSection } from './PersonaDeepDiveModal';
import GlossaryLink from './common/GlossaryLink';

// ── Static KM curve path — memoized so hover state never causes re-animation ──
const KMCurvePath = memo(({ d, strokeColor = '#0EA5E9', strokeWidth = 2 }) => (
  <path
    d={d}
    fill="none"
    stroke={strokeColor}
    strokeWidth={strokeWidth}
    style={{ cursor: 'crosshair', pointerEvents: 'none' }}
  />
));

// Generate mock data as fallback — kept for reference, not currently used
// eslint-disable-next-line no-unused-vars
const generateMockOverallData = (indication) => {
  const timePoints = Array.from({ length: 13 }, (_, i) => i); // 0 to 12 months
  const survivalCurve = timePoints.map(month => {
    if (month === 0) return 1.0;
    // Realistic survival curve that drops over time
    const baseRate = indication === 'NHL' ? 0.65 : 0.58; // NHL typically better than B-ALL
    const dropRate = Math.exp(-month * 0.08); // Exponential decay
    return Math.max(baseRate, dropRate);
  });

  return {
    cohort_size: indication === 'NHL' ? 175 : 88,
    overall_km: {
      n: indication === 'NHL' ? 175 : 88,
      x: timePoints,
      y: survivalCurve
    }
  };
};

const OverallCohortChart = ({
  indication = 'NHL',
  analysisType = 'PFS',
  timeline = '12 Month',
  gradientScheme = 'clinical',
  overallCohortData = null,
  selectedPersonaData = null,
  linesOfFailureData = null,
  onResetToOverall = null,
  filters = null,
  selectedCard = null,
  onLinesDataLoaded = null,
  onHospitalDataReady = null,
  hideHospitalDistribution = false,
  overallSafetyData = null,   // NEW: overall cohort safety data for dashboard
  onStatsReady = null,        // NEW: callback to bubble computed stats up to parent
  userRole = null,            // NEW: role-based CI visibility (superadmin/stakeholder only)
  preloadedOverallLinesData = null, // pre-fetched from RWEDashboard — skips internal fetch
}) => {
  // CRITICAL DEBUG: Check overallCohortData structure (only in development)
  if (process.env.NODE_ENV === 'development' && overallCohortData) {
    console.log('🔍 DEBUG: overallCohortData structure:', {
      'keys': Object.keys(overallCohortData),
      'has kmData': !!overallCohortData.kmData,
      'kmData keys': overallCohortData.kmData ? Object.keys(overallCohortData.kmData) : 'N/A',
      'kmData has at_risk': overallCohortData.kmData ? !!overallCohortData.kmData.at_risk : 'N/A',
      'Full overallCohortData': overallCohortData
    });
  }
  
  // CRITICAL DEBUG: Check what's in selectedPersonaData when it arrives (only in development)
  if (process.env.NODE_ENV === 'development' && selectedPersonaData) {
    console.log('🔍 DEBUG: selectedPersonaData received in OverallCohortChart:', {
      '🔑 ALL KEYS in selectedPersonaData': Object.keys(selectedPersonaData),
      'has kmData?': !!selectedPersonaData.kmData,
      '🔑 ALL KEYS in selectedPersonaData.kmData': selectedPersonaData.kmData ? Object.keys(selectedPersonaData.kmData) : 'N/A',
      'kmData has at_risk?': selectedPersonaData.kmData ? !!selectedPersonaData.kmData.at_risk : 'N/A',
      'kmData.at_risk length': selectedPersonaData.kmData?.at_risk ? selectedPersonaData.kmData.at_risk.length : 'N/A',
      'Full selectedPersonaData': selectedPersonaData
    });
  }

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentView, setCurrentView] = useState('overall'); // 'overall' or 'persona'
  const [linesOfFailure, setLinesOfFailure] = useState(null); // Store lines of failure KM data
  const [animationKey, setAnimationKey] = useState(0); // eslint-disable-line no-unused-vars
  
  // NEW: State for line visibility control
  // Default: only Selected Subgroup visible — user can enable others via checkboxes
  const [lineVisibility, setLineVisibility] = useState({
    overallCohort: false,
    selectedPersona: true,
    linesOfFailure: false
  });

  // ORR state — isolated, does not affect any existing chart/KM logic
  const [orrData, setOrrData] = useState(null);

  // Clear stale ORR data immediately when filter scope changes
  useEffect(() => {
    setOrrData(null);
  }, [
    filters?.activeLevel,
    filters?.selectedInstitute,
    filters?.selectedRegion,
    filters?.selectedPhysician,
    filters?.myPhysicianName,
    indication,
  ]);

  // Overall cohort lines data — for dashboard bar chart (overallMode)
  const [overallLinesData, setOverallLinesData] = useState(null);

  // Clear stale lines data when filter scope changes
  useEffect(() => {
    setOverallLinesData(null);
  }, [
    filters?.activeLevel,
    filters?.selectedInstitute,
    filters?.selectedRegion,
    filters?.selectedPhysician,
    filters?.myPhysicianName,
    filters?.apheresisCutoff,
    filters?.followUpCutoff,
    indication,
  ]);

  // Fetch overall cohort lines of therapy data
  useEffect(() => {
    if (selectedPersonaData || !filters) return;

    // Use pre-loaded data from parent if available
    if (preloadedOverallLinesData) {
      setOverallLinesData(preloadedOverallLinesData);
      return;
    }

    // Don't fetch when no patients
    if (overallCohortData && overallCohortData.totalPatients === 0) return;

    let cancelled = false;

    const fetchLines = async () => {
      try {
        const LINE_CATEGORIES = [
          { id: '1',  label: '1st Line Failure' },
          { id: '2',  label: '2nd Line Failure' },
          { id: '3',  label: '3rd Line Failure' },
          { id: '3+', label: '3+ Lines Failure' },
        ];

        let filter_type = 'global', filter_value = 'all';
        if (filters.activeLevel === 'institute' && filters.selectedInstitute) {
          filter_type = 'account'; filter_value = filters.selectedInstitute;
        } else if (filters.activeLevel === 'region' && filters.selectedRegion) {
          filter_type = 'zone'; filter_value = filters.selectedRegion;
        } else if (filters.activeLevel === 'physician' && filters.selectedPhysician) {
          filter_type = 'hcp'; filter_value = filters.selectedPhysician;
        } else if (filters.activeLevel === 'my_patients' && filters.myPhysicianName) {
          filter_type = 'hcp'; filter_value = filters.myPhysicianName;
        }

        const timelineMonths = parseInt((timeline || '12 Month').split(' ')[0]);

        const results = await Promise.all(
          LINE_CATEGORIES.map(async (cat) => {
            const result = await post(API_ENDPOINTS.survivalAnalysis, {
              indication: indication.toLowerCase() === 'nhl' ? 'nhl' : 'all',
              analysis_type: analysisType.toLowerCase(),
              lines_of_failure: cat.id,
              filter_type, filter_value,
              timeline_months: timelineMonths,
              active_level: filters.activeLevel || 'global',
              selected_institute: filters.selectedInstitute || '',
              selected_regions: filters.selectedRegion ? [filters.selectedRegion] : [],
              selected_physicians: filters.selectedPhysician
                ? [filters.selectedPhysician]
                : filters.myPhysicianName ? [filters.myPhysicianName] : [],
            });

            const km = result?.overall_km_object || result?.overall_km;
            let survivalRate = 0;
            if (km?.x && km?.y) {
              const idx = km.x.findIndex(t => t === timelineMonths);
              if (idx !== -1 && km.y[idx] != null) survivalRate = Math.round(km.y[idx] * 100);
            }

            return { id: cat.id, label: cat.label, n: km?.n || result?.cohort_size || 0, survival_rate: survivalRate, kmData: km || null };
          })
        );

        if (!cancelled) setOverallLinesData(results);
      } catch (err) {
        console.warn('Lines fetch failed:', err.message);
      }
    };

    fetchLines();
    return () => { cancelled = true; };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    selectedPersonaData, indication, analysisType, timeline,
    filters?.activeLevel, filters?.selectedInstitute, filters?.selectedRegion,
    filters?.selectedPhysician, filters?.myPhysicianName,
    filters?.apheresisCutoff, preloadedOverallLinesData,
  ]);

  // Derive displayed ORR: use persona-specific ORR when a persona is selected
  const displayedOrr = (() => {
    if (selectedPersonaData && orrData?.persona_orr_objects?.length > 0) {
      const personaLabel = selectedPersonaData.persona || selectedPersonaData.title;
      const match = orrData.persona_orr_objects.find(p => p.label === personaLabel);
      if (match) return { orr_percent: match.orr_percent, total_day28_evaluated: match.total_day28_evaluated };
    }
    return orrData;
  })();

  // Fetch ORR independently — re-runs only when indication or active filters change
  useEffect(() => {
    let cancelled = false;
    const loadORR = async () => {
      const result = await fetchORRData(filters, indication);
      if (!cancelled) setOrrData(result);
    };
    loadORR();
    return () => { cancelled = true; };
  }, [
    indication,
    filters,
    filters?.activeLevel,
    filters?.selectedInstitute,
    filters?.selectedRegion,
    filters?.selectedPhysician,
    filters?.apheresisCutoff,
  ]);

  useEffect(() => {
    if (!onHospitalDataReady) return;
    if (!filters || (filters.activeLevel !== 'institute' && filters.activeLevel !== 'physician')) {
      const hospitalData = currentView === 'persona' && chartData?.overall_km?.top_5_hospitals
        ? chartData.overall_km.top_5_hospitals
        : overallCohortData?.kmData?.top_5_hospitals || null;
      const total = currentView === 'persona' && chartData?.overall_km?.n
        ? chartData.overall_km.n
        : overallCohortData?.kmData?.n || overallCohortData?.totalPatients || chartData?.cohort_size || 0;
      const subtitle = currentView === 'persona' && chartData?.persona_info?.persona
        ? `For ${chartData.persona_info.persona}`
        : 'Overall cohort distribution';
      onHospitalDataReady({ hospitalData, totalPatients: total, subtitle });
    } else {
      onHospitalDataReady(null);
    }
  }, [chartData, currentView, overallCohortData, filters, onHospitalDataReady]);

  // Update lines of failure data when prop changes
  useEffect(() => {
    if (linesOfFailureData && Array.isArray(linesOfFailureData)) {
      console.log('📊 Lines of failure data received:', {
        count: linesOfFailureData.length,
        lines: linesOfFailureData.map(line => ({
          id: line.id,
          label: line.label,
          n: line.n,
          hasKmData: !!line.kmData
        }))
      });
      setLinesOfFailure(linesOfFailureData);
    } else {
      setLinesOfFailure(null);
    }
  }, [linesOfFailureData]);

  // Reset line visibility when view changes
  useEffect(() => {
    if (currentView === 'overall') {
      // Reset to default when returning to overall view
      setLineVisibility({
        overallCohort: false,
        selectedPersona: true,
        linesOfFailure: false
      });
    }
  }, [currentView]);

  // Toggle line visibility
  const toggleLineVisibility = (lineType) => {
    setLineVisibility(prev => ({
      ...prev,
      [lineType]: !prev[lineType]
    }));
  };

  // Use passed data or fetch from API
  useEffect(() => {
    console.log('🔄 OverallCohortChart useEffect triggered with:', {
      indication,
      analysisType,
      timeline,
      hasOverallCohortData: !!overallCohortData,
      hasSelectedPersonaData: !!selectedPersonaData,
      overallCohortDataDetail: overallCohortData ? {
        hasKmData: !!overallCohortData.kmData,
        kmDataKeys: overallCohortData.kmData ? Object.keys(overallCohortData.kmData) : [],
        kmDataHasAtRisk: overallCohortData.kmData ? !!overallCohortData.kmData.at_risk : false,
        kmDataAtRiskLength: overallCohortData.kmData?.at_risk ? overallCohortData.kmData.at_risk.length : 0,
        kmDataAtRiskSample: overallCohortData.kmData?.at_risk ? overallCohortData.kmData.at_risk.slice(0, 5) : []
      } : null
    });

    const setupChartData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Priority 1: If selectedPersonaData is provided, show persona chart
        if (selectedPersonaData && selectedPersonaData.kmData) {
          console.log('📊 Switching to persona view:', {
            persona: selectedPersonaData.persona,
            patients: selectedPersonaData.patients,
            survivalRate: selectedPersonaData.survivalRate,
            isEmpty: selectedPersonaData.isEmpty
          });

          const personaChartData = {
            cohort_size: selectedPersonaData.patients,
            overall_km: selectedPersonaData.kmData,
            persona_info: {
              title: selectedPersonaData.title,
              persona: selectedPersonaData.persona,
              description: selectedPersonaData.description,
              isEmpty: selectedPersonaData.isEmpty
            }
          };

          setChartData(personaChartData);
          setCurrentView('persona');
          setLoading(false);
          setAnimationKey(k => k + 1);
          return;
        } else if (selectedPersonaData) {
          console.warn('⚠️ selectedPersonaData exists but missing kmData:', {
            selectedPersonaData,
            hasKmData: !!selectedPersonaData.kmData,
            keys: Object.keys(selectedPersonaData)
          });
        }

        // Priority 2: If we have overallCohortData passed as props, use it directly
        console.log('🔍 Checking overallCohortData prop:', {
          overallCohortData: overallCohortData,
          hasKmData: overallCohortData?.kmData ? 'yes' : 'no',
          totalPatients: overallCohortData?.totalPatients,
          pfs: overallCohortData?.pfs
        });

        // If overallCohortData is provided but has 0 patients, show empty state immediately
        // without falling through to a global API fetch that would show unfiltered data
        if (overallCohortData && overallCohortData.totalPatients === 0) {
          console.log('⚠️ overallCohortData has 0 patients — showing empty state');
          setChartData({
            cohort_size: 0,
            overall_km: { n: 0, x: [], y: [] }
          });
          setCurrentView('overall');
          setLoading(false);
          return;
        }

        if (overallCohortData && overallCohortData.kmData) {
          console.log('✅ Using passed overall cohort data');
          const transformedData = {
            cohort_size: overallCohortData.totalPatients,
            overall_km: overallCohortData.kmData  // kmData already contains top_5_hospitals from backend
          };
          setChartData(transformedData);
          setCurrentView('overall');
          setLoading(false);
          setAnimationKey(k => k + 1);
          return;
        }

        // Priority 3: Fetch data using current filters (respects hospital/physician/region selection)
        console.log('⚠️ No overallCohortData prop, fetching from API with current filters');
        const apiFilters = filters ? {
          activeLevel: filters.activeLevel || 'global',
          selectedInstitute: filters.selectedInstitute || '',
          selectedRegion: filters.selectedRegion || '',
          selectedPhysician: filters.selectedPhysician || '',
          myPhysicianName: filters.myPhysicianName || '',
          analysisType: analysisType.toLowerCase(),
          timeline: timeline,
          apheresisCutoff: filters.apheresisCutoff || null,
          followUpCutoff: filters.followUpCutoff || null,
        } : {
          activeLevel: 'global',
          selectedInstitute: '',
          selectedRegion: '',
          selectedPhysician: '',
          myPhysicianName: '',
          analysisType: analysisType.toLowerCase(),
          timeline: timeline
        };

        const apiResult = await fetchSurvivalAnalysis(apiFilters, indication);

        if (apiResult && apiResult.overallCohort && apiResult.overallCohort.kmData) {
          const transformedData = {
            cohort_size: apiResult.overallCohort.totalPatients,
            overall_km: apiResult.overallCohort.kmData
          };
          setChartData(transformedData);
          setCurrentView('overall');
          setLoading(false);
          return;
        }

        // If filtered result has no data, show empty state instead of falling back to global
        if (filters && filters.activeLevel !== 'global') {
          setChartData({
            cohort_size: 0,
            overall_km: { n: 0, x: [], y: [] }
          });
          setLoading(false);
          return;
        }

        // All data sources failed — do NOT fall back to mock data
        // Leave chartData as null so the error/empty state renders
        console.log('❌ All data sources failed — no mock fallback, showing error state');
        setError('No data available for the selected filters.');

      } catch (err) {
        console.error('❌ Error fetching overall cohort data:', err);
        setError(err.message);
        // Do NOT fall back to mock data — leave chartData as null so empty/error state renders
      } finally {
        setLoading(false);
      }
    };

    setupChartData();
  }, [indication, analysisType, timeline, overallCohortData, selectedPersonaData, filters, filters?.apheresisCutoff, filters?.followUpCutoff]);

  // Calculate if we should show the overall baseline (needed outside ChartComponent too)
  const hasOverallBaseline = overallCohortData && overallCohortData.kmData && selectedPersonaData;

  // Bubble computed stats up to parent via onStatsReady — runs when chartData changes.
  // Does NOT wait for ORR — ORR is now fetched at the parent level in parallel.
  useEffect(() => {
    if (!onStatsReady || selectedPersonaData || !chartData?.overall_km) return;

    const km = chartData.overall_km;
    const timelineMonths = parseInt((timeline || '12 Month').split(' ')[0]);
    const isEmpty = km.n === 0 || !km.y || km.y.length === 0;

    const survivalAtTimeline = isEmpty ? null : (() => {
      const idx = km.x?.indexOf(timelineMonths);
      if (idx !== -1 && idx !== undefined) {
        // Exact timepoint exists in data — check if at_risk is 0
        const atRiskArr = km.at_risk;
        if (atRiskArr && Array.isArray(atRiskArr) && idx < atRiskArr.length && atRiskArr[idx] === 0) return 0;
        return Math.round(km.y[idx] * 100);
      }
      // Timepoint not in x array — data doesn't extend to this month
      // Check if last available at_risk is 0 (all patients already had events/censored)
      const lastAtRisk = km.at_risk?.[km.at_risk.length - 1] ?? 0;
      if (lastAtRisk === 0) return 0;
      // Data ends before timepoint but patients are still at risk — show 0%
      const maxX = km.x?.[km.x.length - 1] ?? 0;
      if (maxX < timelineMonths) return 0;
      // Fallback
      return Math.round(km.y[km.y.length - 1] * 100);
    })();

    const medianIndex = isEmpty ? -1 : km.y.findIndex(p => p <= 0.5);
    const medianTime = isEmpty ? null : (medianIndex !== -1 ? km.x[medianIndex] : null); // null = Timepoint Not Reached

    onStatsReady({
      n: km.n,
      survivalAtTimeline,
      medianTime,
      timelineMonths,
      analysisType,
      orrData: displayedOrr,
      isEmpty,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, timeline, analysisType, selectedPersonaData, displayedOrr]);

  // Chart rendering component
  const ChartComponent = ({ data }) => {
    const width = 800;
    const height = 350;
    const margin = { top: 40, right: 80, bottom: 60, left: 80 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // IMPORTANT: When showing persona data, we need BOTH curves:
    // 1. Overall cohort (dotted baseline) - from overallCohortData prop
    // 2. Selected persona (solid line) - from selectedPersonaData prop
    
    // Primary data to display (persona if selected, otherwise overall)
    const timePoints = data.overall_km.x; // [0, 1, 2, 3, ..., 24]
    const survivalData = data.overall_km.y; // [1.0, 0.948, 0.840, ..., 0.396]
    const maxTime = 24; // Always fix x-axis to 24 months regardless of curve truncation

    // Overall cohort baseline data (always available from overallCohortData prop)
    // Note: hasOverallBaseline is defined at component level, not here
    const overallTimePoints = hasOverallBaseline ? overallCohortData.kmData.x : null;
    const overallSurvivalData = hasOverallBaseline ? overallCohortData.kmData.y : null;

    console.log('📊 Plotting chart data:', {
      currentView,
      hasPersonaData: !!selectedPersonaData,
      hasOverallBaseline,
      primaryCurve: {
        timePoints: timePoints.slice(0, 5),
        survivalData: survivalData.slice(0, 5),
        totalPoints: timePoints.length
      },
      baselineCurve: hasOverallBaseline ? {
        timePoints: overallTimePoints.slice(0, 5),
        survivalData: overallSurvivalData.slice(0, 5),
        totalPoints: overallTimePoints.length
      } : 'not shown'
    });

    // Scale functions
    const xScale = (time) => (time / maxTime) * chartWidth;
    const yScale = (survival) => chartHeight - (survival * chartHeight); // survival is already 0-1

    // Generate step-after path for Kaplan-Meier curve (EXACT plotting)
    const generateKMPath = (xData, yData) => {
      if (xData.length === 0) return '';

      let path = `M 0 ${yScale(yData[0])}`;
      for (let i = 1; i < xData.length; i++) {
        const x = xScale(xData[i]);
        const prevY = yScale(yData[i - 1]);
        const currentY = yScale(yData[i]);
        // Step-after: horizontal line then vertical drop
        path += ` L ${x} ${prevY} L ${x} ${currentY}`;
      }
      return path;
    };

    // Generate 95% confidence interval bounds (simple approximation)
    // CI width increases with time as sample size decreases
    const generateCIBounds = (xData, yData, atRiskData) => {
      if (!xData || !yData || xData.length === 0) return { ciLower: [], ciUpper: [] };
      
      const ciLower = [];
      const ciUpper = [];
      
      for (let i = 0; i < yData.length; i++) {
        const survival = yData[i];
        const nAtRisk = atRiskData && atRiskData[i] ? atRiskData[i] : 100;
        
        // Greenwood's formula approximation: SE ≈ S * sqrt(1 / n_at_risk)
        // For 95% CI: ±1.96 * SE
        const se = survival * Math.sqrt(1 / Math.max(nAtRisk, 1));
        const margin = 1.96 * se;
        
        ciLower.push(Math.max(0, survival - margin));
        ciUpper.push(Math.min(1.0, survival + margin));
      }
      
      return { ciLower, ciUpper };
    };

    // Generate a filled polygon path for the CI band (step-after style)
    const generateCIBand = (xData, ciUpper, ciLower) => {
      if (!xData || !ciUpper || !ciLower || xData.length === 0) return '';

      // Forward pass: upper bound (step-after)
      let path = `M 0 ${yScale(ciUpper[0])}`;
      for (let i = 1; i < ciUpper.length; i++) {
        const x = xScale(xData[i]);
        const prevY = yScale(ciUpper[i - 1]);
        const currentY = yScale(ciUpper[i]);
        path += ` L ${x} ${prevY} L ${x} ${currentY}`;
      }

      // Extend to the last x position at lower bound level
      const lastX = xScale(xData[ciLower.length - 1]);
      path += ` L ${lastX} ${yScale(ciLower[ciLower.length - 1])}`;

      // Backward pass: lower bound (step-after, reversed)
      for (let i = ciLower.length - 2; i >= 0; i--) {
        const x = xScale(xData[i]);
        const nextY = yScale(ciLower[i + 1]);
        const currentY = yScale(ciLower[i]);
        path += ` L ${x} ${nextY} L ${x} ${currentY}`;
      }

      path += ' Z';
      return path;
    };

    // Handle mouse events for tooltips
    const handleMouseMove = (event, index) => {
      const svgRect = event.currentTarget.closest('svg').getBoundingClientRect();

      setMousePosition({
        x: event.clientX - svgRect.left,
        y: event.clientY - svgRect.top
      });

      setHoveredPoint({
        index,
        month: timePoints[index],
        survival: Math.round(survivalData[index] * 100) // Convert to percentage for display
      });
    };

    const handleMouseLeave = () => {
      setHoveredPoint(null);
    };

    // CI visibility: disabled for now — re-enable by changing to:
    // const showCI = userRole === 'superadmin' || userRole === 'stakeholder';
    const showCI = false; // TODO: re-enable CI shading when client approves

    // Calculate key statistics using EXACT data
    // Extract timeline months from the timeline prop (e.g., "12 Month" -> 12)
    const timelineMonths = parseInt(timeline.split(' ')[0]);
    
    // Check if data is empty or invalid
    const isEmpty = data.persona_info?.isEmpty || data.overall_km.n === 0 || !survivalData || survivalData.length === 0;
    
    const survivalAtTimeline = isEmpty ? null : (() => {
      const idx = timePoints.indexOf(timelineMonths);
      if (idx === -1) return Math.round(survivalData[Math.min(timelineMonths, survivalData.length - 1)] * 100);
      // Check if at_risk is 0 at this timepoint — data doesn't reach here
      const atRiskArr = data.overall_km.at_risk;
      if (atRiskArr && atRiskArr[idx] === 0) return 0;
      return Math.round(survivalData[idx] * 100);
    })();

    const medianIndex = isEmpty ? -1 : survivalData.findIndex(point => point <= 0.5);
    const medianTime = isEmpty ? null : (medianIndex !== -1 ? timePoints[medianIndex] : null); // null = Timepoint Not Reached

    console.log('📈 Calculated statistics:', {
      timelineMonths,
      survivalAtTimeline,
      medianTime,
      medianIndex
    });

    // Bubble computed stats up to parent (RWEDashboard) for the summary bar
    // REMOVED from here — moved to useEffect at component level to avoid infinite render loop

    return (
      <div className="space-y-4">
        {/* Statistics Cards — moved to RWEDashboard SummaryStatsBar above persona grid */}
        {/* OLD 4-card grid kept here for reference / future use:
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 flex items-start justify-end flex-col">
            <div className="text-sm text-blue-600 font-medium mb-1">Overall Cohort</div>
            <div className="text-4xl font-bold text-blue-900">{data.overall_km.n}</div>
            <div className="text-base text-blue-600 mt-1">
              {isEmpty ? 'No patients available' : 'Patients'}
            </div>
          </div>
          <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-teal-600" />
              <div className="text-sm text-teal-600 font-medium">{timelineMonths}-Month {analysisType}</div>
            </div>
            <div className="text-2xl font-bold text-teal-900">
              {isEmpty || survivalAtTimeline === null || isNaN(survivalAtTimeline) ? '0' : `${survivalAtTimeline}%`}
            </div>
            <div className="text-xs text-teal-600 mt-1">
              {isEmpty ? 'Data not available' : 'Survival Rate'}
            </div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-amber-600" />
              <div className="text-sm text-amber-600 font-medium">Median {analysisType}</div>
            </div>
            <div className="text-2xl font-bold text-amber-900">
              {isEmpty || medianTime === null || !isFinite(medianTime)
                ? (isEmpty ? '0' : 'TNR')
                : medianTime.toFixed(1)}
            </div>
            <div className="text-xs text-amber-600 mt-1">
              {isEmpty ? 'Data not available' : medianTime === null ? 'Timepoint Not Reached' : 'Months'}
            </div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-center gap-2 mb-2">
              <PieChart className="w-4 h-4 text-purple-600" />
              <div className="text-sm text-purple-600 font-medium">28-Day ORR</div>
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {displayedOrr?.orr_percent != null
                ? `${Math.round(displayedOrr.orr_percent)}%`
                : <div className="h-7 w-16 bg-purple-100 rounded-lg animate-pulse" />}
            </div>
            <div className="text-xs text-purple-600 mt-1">
              {displayedOrr?.total_day28_evaluated != null
                ? `n = ${displayedOrr.total_day28_evaluated} evaluated`
                : displayedOrr === null
                  ? <div className="h-3 w-24 bg-purple-100 rounded-full animate-pulse mt-1" />
                  : 'Response Rate'}
            </div>
          </div>
        </div>
        */}

        {/* Chart */}
        <div className="w-full flex justify-center relative bg-white rounded-xl border border-slate-200 p-4">
          {isEmpty ? (
            // Special display for empty subgroups or no data
            <div className="flex flex-col items-center justify-center h-96 text-center px-8">
              <div className="mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-20 h-20 text-slate-400 mx-auto">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-slate-700 mb-3">Data Not Available</div>
              <div className="text-base text-slate-500 max-w-lg mb-2">
                {data.persona_info?.isEmpty 
                  ? `The selected patient subgroup (${data.persona_info.persona}) has no patients in the current dataset.`
                  : 'There is no data available for the selected filters and criteria.'}
              </div>
              <div className="text-sm text-slate-400 mt-4">
                {data.persona_info?.isEmpty 
                  ? 'Please select a different subgroup with patient data to view survival analysis.'
                  : 'Try adjusting your filters or selecting different criteria.'}
              </div>
            </div>
          ) : (
            // Normal chart display
            <div className="w-full flex flex-col items-center justify-center">
              {/* Display Options row + Legend row */}
              {((currentView === 'persona' && (hasOverallBaseline || linesOfFailure)) || (currentView === 'overall')) && (
                <div className="mb-3 w-full border border-slate-200 rounded-lg px-4 py-2 flex flex-col gap-2">
                  {/* Row 1: Checkboxes */}
                  <div className="flex items-center justify-center gap-6 flex-wrap">
                    {/* <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">Display Options:</span> */}
                    {hasOverallBaseline && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={lineVisibility.overallCohort} onChange={() => toggleLineVisibility('overallCohort')} className="w-3.5 h-3.5 accent-slate-500 cursor-pointer" />
                        <span className="text-xs text-slate-700">Overall Cohort</span>
                      </label>
                    )}
                    {currentView === 'overall' && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={lineVisibility.selectedPersona} onChange={() => toggleLineVisibility('selectedPersona')} className="w-3.5 h-3.5 accent-blue-500 cursor-pointer" />
                        <span className="text-xs text-slate-700">Overall Cohort</span>
                      </label>
                    )}
                    {currentView === 'persona' && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={lineVisibility.selectedPersona} onChange={() => toggleLineVisibility('selectedPersona')} className="w-3.5 h-3.5 accent-blue-500 cursor-pointer" />
                        <span className="text-xs text-slate-700">Selected Persona</span>
                      </label>
                    )}
                    {linesOfFailure && linesOfFailure.length > 0 && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={lineVisibility.linesOfFailure} onChange={() => toggleLineVisibility('linesOfFailure')} className="w-3.5 h-3.5 accent-purple-500 cursor-pointer" />
                        <span className="text-xs text-slate-700">Lines of Failure (All {linesOfFailure.filter(l => l.n > 0 && l.kmData && l.kmData.x && l.kmData.y).length})</span>
                      </label>
                    )}
                  </div>
                  {/* Row 2: Legend - all in one line */}
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    {hasOverallBaseline && (
                      <div className="flex items-center gap-1.5">
                        <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#475569" strokeWidth="2" strokeDasharray="6,3" /></svg>
                        <span className="text-xs text-slate-500">Overall Cohort</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#0EA5E9" strokeWidth="2.5" /></svg>
                      <span className="text-xs font-medium text-teal-700">{currentView === 'overall' ? 'Overall Cohort' : 'Selected Subgroup'}</span>
                    </div>
                    {linesOfFailure && linesOfFailure.filter(l => l.n > 0 && l.kmData && l.kmData.x && l.kmData.y).map((line, idx) => {
                      const lineColors = ['#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                      const color = lineColors[idx] || '#6b7280';
                      return (
                        <div key={line.id} className="flex items-center gap-1.5">
                          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke={color} strokeWidth="2" /></svg>
                          <span className="text-xs font-medium" style={{ color }}>{line.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Check if any lines are visible */}
              {(() => {
                const hasVisibleLines = 
                  lineVisibility.selectedPersona || 
                  (hasOverallBaseline && lineVisibility.overallCohort) || 
                  (linesOfFailure && lineVisibility.linesOfFailure);

                if (!hasVisibleLines) {
                  // Show empty state when no lines are selected
                  return (
                    <div className="flex flex-col items-center justify-center h-96 text-center px-8">
                      <div className="mb-6">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-20 h-20 text-slate-400 mx-auto">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                      </div>
                      <div className="text-2xl font-bold text-slate-700 mb-3">No Lines Selected</div>
                      <div className="text-base text-slate-500 max-w-lg mb-2">
                        Please select at least one line from the Display Options above to view the survival curve.
                      </div>
                      <div className="text-sm text-slate-400 mt-4">
                        Check one or more boxes to display the corresponding survival curves.
                      </div>
                    </div>
                  );
                }

                // Render the chart if lines are visible
                return (
                  <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="rounded-lg" style={{ maxWidth: width }}>
                    <g transform={`translate(${margin.left}, ${margin.top})`}>
                      {/* Horizontal grid lines */}
                      {[0, 0.25, 0.5, 0.75, 1.0].map(y => (
                        <line
                          key={y}
                          x1={0}
                          y1={yScale(y)}
                          x2={chartWidth}
                          y2={yScale(y)}
                          stroke="#f1f5f9"
                          strokeWidth={1}
                        />
                      ))}
                      {/* Vertical grid lines - use actual time points */}
                      {[0, 6, 12, 18, 24].map(month => (
                        <line
                          key={month}
                          x1={xScale(month)}
                          y1={0}
                          x2={xScale(month)}
                          y2={chartHeight}
                          stroke="#f1f5f9"
                          strokeWidth={1}
                        />
                      ))}

                      {/* ── CI Shading: Main curve (rendered first, behind the line) ── */}
                      {showCI && lineVisibility.selectedPersona && (() => {
                        const { ciLower, ciUpper } = generateCIBounds(timePoints, survivalData, data.overall_km.at_risk);
                        return (
                          <path
                            key={`ci-main-${data.overall_km.n}-${indication}`}
                            d={generateCIBand(timePoints, ciUpper, ciLower)}
                            fill="#0EA5E9"
                            stroke="none"
                            className="ci-fade-once"
                          />
                        );
                      })()}

                      {/* BASELINE: Overall Cohort curve (dotted) - shown when persona is selected */}
                      {hasOverallBaseline && lineVisibility.overallCohort && (
                        <>
                          {/* CI shading for overall cohort */}
                          {showCI && (() => {
                            const { ciLower, ciUpper } = generateCIBounds(overallTimePoints, overallSurvivalData, overallCohortData.kmData.at_risk);
                            return (
                              <path
                                d={generateCIBand(overallTimePoints, ciUpper, ciLower)}
                                fill="#475569"
                                fillOpacity={0.12}
                                stroke="none"
                              />
                            );
                          })()}
                          <path
                            d={generateKMPath(overallTimePoints, overallSurvivalData)}
                            fill="none"
                            stroke="#475569"
                            strokeWidth={1.5}
                            strokeDasharray="8,4"
                            opacity={0.85}
                          />
                          {/* Overall cohort data points removed per client request */}
                        </>
                      )}

                      {/* MAIN: Kaplan-Meier curve (solid) - memoized to prevent re-animation on hover */}
                      {lineVisibility.selectedPersona && (
                        <KMCurvePath
                          d={generateKMPath(timePoints, survivalData)}
                        />
                      )}

                      {/* LINES OF FAILURE: Render each line of therapy failure curve */}
                      {lineVisibility.linesOfFailure && linesOfFailure && linesOfFailure
                        .filter(l => l.n > 0 && l.kmData && l.kmData.x && l.kmData.y)
                        .map((line, idx) => {
                        const lineColors = ['#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                        const color = lineColors[idx] || '#6b7280';
                        const { ciLower, ciUpper } = generateCIBounds(line.kmData.x, line.kmData.y, line.kmData.at_risk);
                        
                        return (
                          <g key={`line-${line.id}`}>
                            {/* CI shading for this line of failure */}
                            {showCI && (
                              <path
                                d={generateCIBand(line.kmData.x, ciUpper, ciLower)}
                                fill={color}
                                fillOpacity={0.12}
                                stroke="none"
                              />
                            )}
                            <path
                              d={generateKMPath(line.kmData.x, line.kmData.y)}
                              fill="none"
                              stroke={color}
                              strokeWidth={1.5}
                              opacity={0.6}
                              style={{ cursor: 'crosshair' }}
                            />
                          </g>
                        );
                      })}

                      {/* Interactive hover areas */}
                      {timePoints.map((time, index) => (
                        <rect
                          key={`hover-${index}`}
                          x={index === 0 ? 0 : xScale(time) - 10}
                          y={0}
                          width={index === 0 ? 10 : 20}
                          height={chartHeight}
                          fill="transparent"
                          style={{ cursor: 'crosshair' }}
                          onMouseMove={(e) => handleMouseMove(e, index)}
                          onMouseLeave={handleMouseLeave}
                        />
                      ))}

                      {/* Data points removed per client request — hover still works via invisible rect areas below */}

                      {/* Axes */}
                      <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke="#64748b" strokeWidth={2} />
                      <line x1={0} y1={0} x2={0} y2={chartHeight} stroke="#64748b" strokeWidth={2} />

                      {/* X-axis labels - Dynamic based on actual data length */}
                      {(() => {
                        // Generate x-axis ticks based on maxTime
                        const generateXTicks = (max) => {
                          if (max <= 6) return [0, 3, 6].filter(t => t <= max);
                          if (max <= 12) return [0, 3, 6, 9, 12].filter(t => t <= max);
                          if (max <= 18) return [0, 6, 12, 18].filter(t => t <= max);
                          return [0, 6, 12, 18, 24].filter(t => t <= max);
                        };
                        
                        const xTicks = generateXTicks(maxTime);
                        
                        return xTicks.map(month => (
                          <text
                            key={month}
                            x={xScale(month)}
                            y={chartHeight + 20}
                            textAnchor="middle"
                            fontSize={12}
                            fill="#64748b"
                          >
                            {month}
                          </text>
                        ));
                      })()}

                      {/* Y-axis labels */}
                      {[0, 25, 50, 75, 100].map(percent => (
                        <text
                          key={percent}
                          x={-15}
                          y={yScale(percent / 100) + 4}
                          textAnchor="end"
                          fontSize={12}
                          fill="#64748b"
                        >
                          {percent}%
                        </text>
                      ))}

                      {/* Interactive Tooltip */}
                      {hoveredPoint && (() => {
                        // Collect all active lines data for this time point
                        const activeLines = [];

                        // Main curve
                        if (lineVisibility.selectedPersona) {
                          activeLines.push({
                            label: currentView === 'persona'
                              ? (chartData?.persona_info?.persona?.split(' | ')[0] || 'Selected')
                              : 'Overall',
                            value: hoveredPoint.survival,
                            color: '#0EA5E9',
                          });
                        }

                        // Lines of failure
                        if (lineVisibility.linesOfFailure && linesOfFailure) {
                          const lineColors = ['#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                          linesOfFailure
                            .filter(l => l.n > 0 && l.kmData && l.kmData.x && l.kmData.y)
                            .forEach((line, idx) => {
                            const ptIdx = line.kmData.x.findIndex(t => t === hoveredPoint.month);
                            if (ptIdx !== -1) {
                              activeLines.push({
                                label: line.label || `Line ${idx + 1}`,
                                value: Math.round(line.kmData.y[ptIdx] * 100),
                                color: lineColors[idx] || '#6b7280',
                              });
                            }
                          });
                        }

                        // Tooltip dimensions
                        const tooltipW = 130;
                        const lineH = 18;
                        const headerH = 24; // month header height
                        const dividerH = 10; // space for divider
                        const paddingY = 10;
                        const tooltipH = paddingY + headerH + dividerH + lineH * activeLines.length + paddingY;

                        // Smart X positioning — flip left when near right edge
                        const nearRightEdge = mousePosition.x > chartWidth * 0.75;
                        const tooltipX = nearRightEdge ? -(tooltipW + 10) : 10;

                        // Smart Y positioning — flip up when near bottom
                        const nearBottom = mousePosition.y > chartHeight * 0.75;
                        const tooltipY = nearBottom ? -(tooltipH + 5) : -tooltipH / 2;

                        return (
                          <g transform={`translate(${mousePosition.x}, ${mousePosition.y})`}>
                            <rect
                              x={tooltipX}
                              y={tooltipY}
                              width={tooltipW}
                              height={tooltipH}
                              fill="white"
                              stroke="#e2e8f0"
                              strokeWidth={1}
                              rx={6}
                              filter="drop-shadow(0 4px 6px rgba(0,0,0,0.12))"
                            />
                            {/* Month header */}
                            <text
                              x={tooltipX + tooltipW / 2}
                              y={tooltipY + paddingY + 14}
                              textAnchor="middle"
                              fontSize={11}
                              fontWeight="bold"
                              fill="#374151"
                            >
                              {hoveredPoint.month} months
                            </text>
                            {/* Divider */}
                            <line
                              x1={tooltipX + 8}
                              y1={tooltipY + paddingY + headerH + 4}
                              x2={tooltipX + tooltipW - 8}
                              y2={tooltipY + paddingY + headerH + 4}
                              stroke="#e2e8f0"
                              strokeWidth={1}
                            />
                            {/* Lines */}
                            {activeLines.map((line, i) => (
                              <text
                                key={i}
                                x={tooltipX + tooltipW / 2}
                                y={tooltipY + paddingY + headerH + dividerH + lineH * i + 13}
                                textAnchor="middle"
                                fontSize={11}
                                fill={line.color}
                                fontWeight="600"
                              >
                                {analysisType}: {line.value}%
                              </text>
                            ))}
                          </g>
                        );
                      })()}
                    </g>

                    {/* Axis titles */}
                    <text
                      x={width / 2}
                      y={height - 10}
                      textAnchor="middle"
                      fontSize={14}
                      fontWeight="bold"
                      fill="#374151"
                    >
                      Time (Months)
                    </text>

                    <text
                      x={20}
                      y={height / 2}
                      textAnchor="middle"
                      fontSize={14}
                      fontWeight="bold"
                      fill="#374151"
                      transform={`rotate(-90, 20, ${height / 2})`}
                    >
                      {analysisType} %
                    </text>
                  </svg>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <span className="text-slate-600">Loading overall cohort curve...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !chartData) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 mb-6">
        <div className="flex items-center gap-3 text-red-600">
          <Info className="w-5 h-5" />
          <div>
            <div className="font-medium">Unable to load overall cohort data</div>
            <div className="text-sm text-red-500 mt-1">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
      {/* Header — only shows reset button and error badge, heading moved below LOT/Safety cards */}
      <div className="flex items-center justify-between mb-3">
        <div>
          {/* Reset Button - hidden inside PersonaDeepDiveModal, only shown on dashboard */}
          {currentView === 'persona' && onResetToOverall && (
            <button
              onClick={onResetToOverall}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              ← Back to Overall Cohort
            </button>
          )}
        </div>

        {error && (
          <div className="ml-auto">
            <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
              Using fallback data
            </div>
          </div>
        )}
      </div>

      {/* Lines of Therapy + Safety Profile — bar chart layout (dashboard only, overall cohort) */}
      {!selectedPersonaData && filters && chartData && chartData.overall_km && chartData.overall_km.n > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
          <LinesOfTherapySection linesData={preloadedOverallLinesData || overallLinesData} totalN={chartData.overall_km.n} analysisType={analysisType} timeline={timeline} />
          <SafetyProfileSection bucket={overallSafetyData} />
        </div>
      )}

      {/* KM Curve heading — sits below LOT/Safety cards, above the chart */}
      {chartData && chartData.overall_km && chartData.overall_km.n > 0 && (
        <h3 className="text-base font-bold text-slate-800 tracking-wide mb-3">
          {currentView === 'persona' && chartData?.persona_info
            ? `${chartData.persona_info.persona}`
            : 'Kaplan-Meier Curve Analysis'
          }
          {' '}<GlossaryLink section="km" tooltip="The Kaplan-Meier (KM) method estimates the probability of remaining event-free over time, accounting for patients with incomplete follow-up (censored). The curve begins at 100% and steps down at each event; its height at a given month represents the estimated survival probability.

Progression-Free Survival (PFS) curve — plots the estimated percentage of patients alive and progression-free at each month after NexCAR19 infusion, derived using Kaplan-Meier analysis.

Overall Survival (OS) curve — plots the estimated percentage of patients alive at each month after NexCAR19 infusion, derived using Kaplan-Meier analysis." />
        </h3>
      )}

      {/* Chart Content — only render when there are patients */}
      {chartData && chartData.overall_km && chartData.overall_km.n > 0 && <ChartComponent data={chartData} />}

      {/* Empty state — shown when cohort has 0 patients (e.g. physician with no patients) */}
      {chartData && chartData.overall_km && chartData.overall_km.n === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center px-8">
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-16 h-16 text-slate-300 mx-auto">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
            </svg>
          </div>
          <div className="text-lg font-semibold text-slate-600 mb-2">No Survival Data Available</div>
          <div className="text-sm text-slate-400 max-w-md">
            There are no patients matching the selected filters. Adjust your filters to view the Kaplan-Meier curve and survival statistics.
          </div>
        </div>
      )}

      {/* Numbers at Risk Table - Using Real API Data */}
      {chartData && chartData.overall_km && chartData.overall_km.n > 0 && (
        <RiskTable 
          timePoints={[0, 3, 6, 9, 12, 15, 18, 24]}
          cohorts={(() => {
            console.log('🔢 Building Risk Table with REAL API data:', {
              currentView,
              hasOverallCohortData: !!overallCohortData,
              hasSelectedPersonaData: !!selectedPersonaData,
              hasLinesOfFailure: !!linesOfFailure,
              lineVisibility,
              overallCohortDataStructure: overallCohortData ? {
                hasKmData: !!overallCohortData.kmData,
                kmDataKeys: overallCohortData.kmData ? Object.keys(overallCohortData.kmData) : [],
                hasAtRisk: !!overallCohortData.kmData?.at_risk
              } : null
            });

            // Validate KM objects before processing
            // IMPORTANT: Check if kmData is the actual KM object or if it's nested
            let overallKM = null;
            if (overallCohortData?.kmData) {
              console.log('🔍 DEBUG: overallCohortData.kmData structure:', {
                'keys': Object.keys(overallCohortData.kmData),
                'has at_risk': !!overallCohortData.kmData.at_risk,
                'at_risk length': overallCohortData.kmData.at_risk ? overallCohortData.kmData.at_risk.length : 'N/A'
              });
              
              // If kmData is an array, it might be the wrong structure
              if (Array.isArray(overallCohortData.kmData)) {
                console.warn('⚠️ overallCohortData.kmData is an array, expected object');
                console.warn('Array contents:', overallCohortData.kmData);
              } else if (isValidKMObject(overallCohortData.kmData)) {
                overallKM = overallCohortData.kmData;
                console.log('✅ overallKM validated and assigned');
              } else {
                console.warn('⚠️ overallCohortData.kmData failed validation - missing at_risk, will not show overall baseline');
              }
            } else {
              console.log('⚠️ overallCohortData.kmData does not exist - will not show overall baseline');
            }
            
            const personaKM = chartData?.overall_km && isValidKMObject(chartData.overall_km)
              ? chartData.overall_km
              : null;

            // IMPORTANT: When in 'overall' view, only show the main cohort data
            // When in 'persona' view, show both overall baseline and selected persona
            const shouldShowOverallBaseline = currentView === 'persona' && lineVisibility.overallCohort && overallKM;
            const shouldShowPersonaData = currentView === 'overall' || (currentView === 'persona' && lineVisibility.selectedPersona);

            // CRITICAL DEBUG: Check what's being passed to buildRiskTableCohorts
            console.log('🔍 DEBUG: Data being passed to buildRiskTableCohorts:', {
              currentView,
              shouldShowOverallBaseline,
              shouldShowPersonaData,
              'overallKM keys': overallKM ? Object.keys(overallKM) : 'null',
              'overallKM has at_risk': overallKM ? !!overallKM.at_risk : 'N/A',
              'personaKM keys': personaKM ? Object.keys(personaKM) : 'null',
              'personaKM has at_risk': personaKM ? !!personaKM.at_risk : 'N/A',
              'personaKM.at_risk length': personaKM?.at_risk ? personaKM.at_risk.length : 'N/A'
            });

            // Build cohorts using real API data
            const cohorts = buildRiskTableCohorts({
              overallKM: shouldShowOverallBaseline ? overallKM : null,
              personaKM: shouldShowPersonaData ? personaKM : null,
              linesOfFailureKM: lineVisibility.linesOfFailure ? linesOfFailure : [],
              visibility: lineVisibility,
              timePoints: [0, 3, 6, 9, 12, 15, 18, 24]
            });

            console.log('✅ Risk Table cohorts built:', {
              count: cohorts.length,
              currentView,
              shouldShowOverallBaseline,
              shouldShowPersonaData,
              cohorts: cohorts.map(c => ({
                id: c.id,
                name: c.name,
                atRiskSample: c.atRisk.slice(0, 3)
              }))
            });

            return cohorts;
          })()}
          timeUnit="months"
          showTable={true}
          showTimeFooter={lineVisibility.linesOfFailure && linesOfFailure && linesOfFailure.length > 0}
        />
      )}

      {/* Hospital Distribution Chart — hidden for sales/physician roles and for hospital/physician level filters */}
      {!hideHospitalDistribution && (!filters || (filters.activeLevel !== 'institute' && filters.activeLevel !== 'physician')) && (
        <div className="mt-6">
          <HospitalDistributionChart
            hospitalData={
              currentView === 'persona' && chartData?.overall_km?.top_5_hospitals
                ? chartData.overall_km.top_5_hospitals
                : overallCohortData?.kmData?.top_5_hospitals || null
            }
            totalPatients={
              currentView === 'persona' && chartData?.overall_km?.n
                ? chartData.overall_km.n
                : overallCohortData?.totalPatients || 0
            }
            title="Hospital Distribution"
            subtitle={
              currentView === 'persona' && chartData?.persona_info?.persona
                ? `For ${chartData.persona_info.persona}`
                : 'Overall cohort distribution'
            }
            userRole={userRole}
            indication={indication}
            analysisType={analysisType}
            timeline={timeline}
            filters={filters}
          />
        </div>
      )}

    </div>
  );
};

export default OverallCohortChart;
